import { FC, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { MainLayout } from 'views/layouts'
import { ModulesQuestionContainer } from './styled'
import { useNavigate } from 'react-router-dom'
import { Formik } from 'formik'
import Input from 'views/components/UI/TextInput'
import Select from 'views/components/UI/Select'
import TitleDiv from 'views/components/UI/TitleDiv'
import { Button } from 'views/components/UI/Buttons'
import { useNotification } from 'lib/context/notification.context'
import { Row } from 'views/components/UI/Row'
import { generalDataSelector } from 'ducks/auth'
import { rootProcessSelector, setUserListAdded } from 'ducks/createProcess' //setStepFive,
import { userSelector } from 'ducks/user'
import useCreateUser from 'hooks/users/useCreateUser/useCreateUser'
import { platformAdminRoutes } from 'router/routes'
import { validationSchema } from './schema'
import { updateProcess } from 'services/common/process/createProcess/create-process-service'
import { useTranslation } from 'react-i18next'
import { PageTitle } from 'views/components/UI/Text'
import Modal from 'views/components/UI/Modal'
import AddUserProgressDialog from '../add-user-progress-dialog'
import { getAddUserErrorMessage } from '../user-credits'
import { createMultipleUsersService } from 'views/screens/Platform-administrator/services'
import { isAxiosError } from 'axios'

const USER_EVALUATED_ROLE_TYPE = 4

const FormCreateUser: FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { getError, getSuccess, getWarning } = useNotification()
  const { createUser, loadingCreateUser } = useCreateUser()
  const { companyId } = useSelector(userSelector)
  const stepOneData = useSelector(rootProcessSelector).process.stepOne
  const stepFiveData = useSelector(rootProcessSelector).process.stepFive
  const { t } = useTranslation()
  const { data: generalData } = useSelector(generalDataSelector)

  const [documenTypeOptions, setDocumentTypeOptions] = useState<any>([])
  const [showExistingUserModal, setShowExistingUserModal] = useState(false)
  const [existingUserMessage, setExistingUserMessage] = useState('')
  const [progress, setProgress] = useState('')

  useEffect(() => {
    if (generalData?.document_type) {
      const documentTypes = generalData.document_type.map((item: any) => {
        return {
          value: item.id,
          label: item.name,
        }
      })
      setDocumentTypeOptions(documentTypes)
    }
  }, [generalData?.document_type])

  const handleCreate = async (values: any, confirmExistingUser: boolean) => {
    setProgress(confirmExistingUser ? 'Agregando el usuario al proceso...' : 'Comprobando documento y correo...')
    try {
      const response = await createUser({
      ...values,
      roleType: USER_EVALUATED_ROLE_TYPE,
      companyId,
      noSendEmail: true,
      confirmExistingUser,
      processId: stepOneData.processId,
    })
        if (response.status === 'error') {
          if (response.code === 'EXISTING_USER_CONFIRMATION_REQUIRED') {
            setExistingUserMessage(response.message)
            setShowExistingUserModal(true)
            return
          }
          getError(response.message)
          return
        }

        const previousUsers = stepFiveData?.userListAdded || []
        if (previousUsers.some((user: { id: number }) => user.id === response.data.user_id)) {
          getWarning('Este usuario ya está agregado a este proceso.')
          return
        }
        setProgress('Agregando el usuario al proceso...')
        try {
          if (response.data.existing_user) {
            const batchResponse = await createMultipleUsersService({
              process_id: stepOneData.processId,
              users: [{
                first_name: values.firstName,
                last_name: values.lastName,
                document: values.document.trim(),
                email: values.email.trim().toLowerCase(),
                document_type: Number(values.documentType),
                role_type: USER_EVALUATED_ROLE_TYPE,
                company_id: companyId,
                expected_user_id: response.data.user_id,
              }],
            })
            if (
              batchResponse.data.existingUsers.length !== 1 ||
              Number(batchResponse.data.existingUsers[0].user_id) !== Number(response.data.user_id)
            ) {
              throw new Error('No se pudo confirmar la incorporación del usuario existente.')
            }
          } else {
            const dataToSaveProcess = {
              users: {
                process_id: stepOneData.processId,
                subscription_company_id: stepOneData.inventoryTypeId,
                users_id: [response.data.user_id],
              },
            }
            const processResponse = await updateProcess(stepOneData.processId, dataToSaveProcess)
            if (processResponse.status !== 'OK') {
              getError(processResponse.error || 'No se pudo agregar el usuario al proceso.')
              return
            }
          }

          dispatch(
            setUserListAdded([
              ...previousUsers,
                {
                  id: response.data.user_id,
                  document: response.data.document,
                  email: response.data.mail_address,
                  first_name: response.data.user_name,
                },
            ])
          )
          getSuccess(t('global.notifications.addUserSuccess'))
          navigate(platformAdminRoutes.CreateUsers, { state: { newUserId: response.data.user_id } })
        } catch (error) {
          console.error('Error al crear el usuario:', error)
          const rejectedReason = isAxiosError<{ failedUsers?: Array<{ reason?: string }> }>(error)
            ? error.response?.data?.failedUsers?.[0]?.reason
            : undefined
          getError(
            rejectedReason || getAddUserErrorMessage(error)
          )
        }
    } catch (error) {
      console.error('Error al crear el usuario:', error)
      getError('No se pudo comprobar ni agregar el usuario.')
    } finally {
      setProgress('')
    }
  }

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        documentType: '',
        document: '',
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => handleCreate(values, false)}
    >
      {({ values, errors, touched, handleChange, setFieldValue, handleBlur, handleSubmit }) => (
        <MainLayout>
          <TitleDiv>
            <PageTitle margin="0px 0px 30px 0px">Crear nuevo usuario</PageTitle>
          </TitleDiv>
          <h4 style={{ margin: 0 }}>Información del usuario</h4>
          <form onSubmit={handleSubmit}>
            <ModulesQuestionContainer>
              <Input
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                type="text"
                name="firstName"
                label="Nombre"
                helperText={(touched.firstName && errors.firstName) || ''}
              />
              <Input
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                type="text"
                name="lastName"
                label="Apellido"
                helperText={(touched.lastName && errors.lastName) || ''}
              />
              <Input
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                type="text"
                name="email"
                label="Correo electrónico"
                helperText={(touched.email && errors.email) || ''}
              />
              <Select
                label="Tipo de documento"
                options={documenTypeOptions}
                value={values.documentType}
                onChange={(value: any) => {
                  setFieldValue('documentType', value)
                }}
                name="documentType"
                error={(touched.documentType && (errors.documentType as string)) || ''}
                hideNeverChoice
              />
              <Input
                value={values.document}
                onChange={handleChange}
                onBlur={handleBlur}
                type="text"
                name="document"
                label="Número de documento"
                helperText={(touched.document && errors.document) || ''}
              />
            </ModulesQuestionContainer>
            <Row alignItems="center" justifyContent="space-between">
              <span
                style={{ fontWeight: 'bold', color: '#2D749C', cursor: 'pointer' }}
                onClick={() => {
                  navigate(-1)
                }}
              >
                Cancelar
              </span>
              <Button type="submit" disabled={loadingCreateUser || Object.keys(errors).length > 0}>
                {loadingCreateUser ? 'Creando...' : 'Crear'}
              </Button>
            </Row>
          </form>
          <Modal
            show={showExistingUserModal}
            setShow={setShowExistingUserModal}
            hideCloseButton
            styleContent={{ width: '500px' }}
          >
            <h3>El usuario ya existe</h3>
            <p>{existingUserMessage}</p>
            <Row alignItems="center" justifyContent="space-between">
              <Button
                type="button"
                onClick={() => {
                  setShowExistingUserModal(false)
                  getWarning('Si quieres hacerlo manualmente, vuelve a la ventana anterior y usa el buscador.')
                }}
              >
                No
              </Button>
              <Button
                type="button"
                disabled={loadingCreateUser}
                onClick={() => {
                  setShowExistingUserModal(false)
                  handleCreate(values, true)
                }}
              >
                Sí, añadir
              </Button>
            </Row>
          </Modal>
          <AddUserProgressDialog
            isOpen={Boolean(progress)}
            title="Comprobando usuario"
            progress={progress}
          />
        </MainLayout>
      )}
    </Formik>
  )
}
export default FormCreateUser
